import { TUnitSystem } from "./types"

/**
 * Dimension formatting ported 1:1 from iOS.
 * iOS sources of truth:
 * - Sources/Review/HelperFunctions.swift (twoUnitsDimensionString / dimensionString / simpleDimensionString)
 * - Sources/Review/ArtUnits.swift (InToCm = 2.54, metricToImperial)
 * - Sources/Review/Extensions/String.swift (tidyUpDecimal)
 *
 * NOTE: deliberately NOT reusing @utilities/workProperties/dimensions.ts —
 * it uses a 2.56 cm/in ratio and different rounding; the PDF must match iOS (2.54).
 */

const inToCm = 2.54

const unitShortName: Record<TUnitSystem, string> = {
	metric: "cm",
	imperial: "in",
}

// iOS String.tidyUpDecimal(): round to 1 decimal, drop ".0"
export function tidyUpDecimal(value: string): string {
	const parsed = parseFloat(value)
	const number = isNaN(parsed) ? 0 : Math.round(parsed * 10) / 10
	return Number.isInteger(number) ? String(Math.round(number)) : String(number)
}

// iOS ArtUnits.metricToImperial: round(100 / 2.54 * v) / 100
function metricToImperial(value: number): number {
	return Math.round((100 / inToCm) * value) / 100
}

// iOS HelperFunctions.simpleDimensionString — note HEIGHT comes first
function simpleDimensionString(units: TUnitSystem, width: string, height: string, depth: string): string {
	let output: string
	if (depth === "" || depth === "0" || depth === "0.0") {
		output = `${tidyUpDecimal(height)} x ${tidyUpDecimal(width)}`
	} else {
		output = `${tidyUpDecimal(height)} x ${tidyUpDecimal(width)} x ${tidyUpDecimal(depth)}`
	}
	return `${output} ${unitShortName[units]}`
}

// iOS HelperFunctions.dimensionString
export function dimensionString(width: string, height: string, depth: string, unitSystem: TUnitSystem): string {
	let widthx = parseFloat(width) || 0
	let heightx = parseFloat(height) || 0
	let depthx = parseFloat(depth) || 0

	if (unitSystem === "imperial") {
		widthx = metricToImperial(widthx)
		heightx = metricToImperial(heightx)
		depthx = metricToImperial(depthx)
	}

	return simpleDimensionString(unitSystem, String(widthx), String(heightx), String(depthx))
}

// iOS HelperFunctions.twoUnitsDimensionString — preferred unit first, other in parentheses
export function twoUnitsDimensionString(
	width: string,
	height: string,
	depth: string,
	prefUnit: TUnitSystem = "metric"
): string {
	if (prefUnit === "imperial") {
		return `${dimensionString(width, height, depth, "imperial")} (${dimensionString(width, height, depth, "metric")})`
	}
	return `${dimensionString(width, height, depth, "metric")} (${dimensionString(width, height, depth, "imperial")})`
}
